import { resolveLocalizedText } from '@seaa/shared/localized-text'
import { createContext, useContext, useEffect, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { type PublicSiteContext, type PublicSiteContextResponse } from '@seaa/shared'
import { api } from './api'
import { useI18n } from './i18n'
import { parseCustomerAppOrigin } from './canonical-customer-url'

interface SiteContextValue {
  site: PublicSiteContext | null
  isLoading: boolean
  potentialCustomHost: boolean
  customerAppOrigin: string | null
  isError: boolean
}

const SiteContext = createContext<SiteContextValue>({ site: null, isLoading: true, potentialCustomHost: false, customerAppOrigin: null, isError: false })

function isPotentialCustomHost(value: string) {
  const hostname = value.toLowerCase()
  const configured = (import.meta.env.VITE_PLATFORM_HOSTS || '127.0.0.1,localhost').split(',').map((value: string) => value.trim().toLowerCase())
  return !configured.includes(hostname)
}

export function SiteContextProvider({ children, initialSite = null, initialCustomerAppOrigin, siteResolved = false, hostname }: {
  children: ReactNode
  initialSite?: PublicSiteContext | null
  siteResolved?: boolean
  hostname?: string
  initialCustomerAppOrigin?: string | undefined
}) {
  const { applySiteLocale, locale } = useI18n()
  const resolvedHostname = hostname ?? (typeof window === 'undefined' ? 'localhost' : window.location.hostname)
  const potentialCustomHost = isPotentialCustomHost(resolvedHostname)
  const query = useQuery({
    queryKey: ['public-site-context', resolvedHostname],
    queryFn: () => api<PublicSiteContextResponse>('/api/public/site-context'),
    initialData: siteResolved && initialCustomerAppOrigin ? { site: initialSite, customerAppOrigin: initialCustomerAppOrigin } : undefined,
    staleTime: 5 * 60_000,
    retry: false,
  })
  const site = query.data?.site ?? initialSite

  useEffect(() => {
    if (!site) return
    const websiteDesign = window.location.pathname === '/' ? site.storefrontDesign : undefined
    const enabledLocales = (websiteDesign?.enabledLocales ?? site.enabledLocales).filter((value) => ['de', 'en', 'nl', 'ar'].includes(value))
    const launchLocales: PublicSiteContext['enabledLocales'] = enabledLocales.length > 0 ? enabledLocales : ['ar', 'en']
    const preferredLocale = websiteDesign?.defaultLocale ?? site.defaultLocale
    const defaultLocale = launchLocales.includes(preferredLocale) ? preferredLocale : launchLocales[0]!
    applySiteLocale(defaultLocale, launchLocales)
    const root = document.documentElement
    root.style.setProperty('--sanad-accent', site.accentColor)
    root.style.setProperty('--sanad-accent-strong', site.accentColor)
    root.style.setProperty('--sanad-clay', site.accentColor)
    root.style.setProperty('--sanad-clay-dark', site.accentColor)
    root.style.setProperty('--color-accent-ink', site.accentContrastColor)
    return () => {
      for (const name of ['--sanad-accent','--sanad-accent-strong','--sanad-clay','--sanad-clay-dark','--color-accent-ink']) root.style.removeProperty(name)
    }
  }, [applySiteLocale, site])

  useEffect(() => {
    if (site && document.documentElement.dataset.titleManaged !== 'true') {
      document.title = `${resolveLocalizedText(site.publicTitleLocales, locale, site.publicTitle)} · ${site.city}`
    }
  }, [locale, site])

  return <SiteContext.Provider value={{ site, isLoading: query.isLoading, potentialCustomHost, customerAppOrigin: parseCustomerAppOrigin(query.data?.customerAppOrigin), isError: query.isError }}>{children}</SiteContext.Provider>
}

export function useSiteContext() {
  return useContext(SiteContext)
}
